import React, {useCallback, useState, useEffect} from 'react';
import {Select} from 'antd';
import config from "../config";
import {useDebounce} from "rooks";
import useAllStops from '../hooks/useAllStops';
import DisplayStop from './DisplayStop';
import { snapToRoadOsrm } from '../libs/pathLib';

const LocationLookup = ({location, setLocation, limit = 5, allStops, ...props}) => {
    const [options, setOptions] = useState(location? [location] : []);
    const [searchValue, setSearchValue] = useState('');
    const [loading, setLoading] = useState(false);

    // Create a debounced search function
    const debouncedSearch = useDebounce(async (value) => {
        if (!value) {
            setOptions(location? [location] : []);
            setLoading(false);
            return;
        }

        setLoading(true);
        // Immediately show local stops
        let localOptions = [];
        if (allStops) {
            const searchLower = value.toLowerCase();
            const matchingStops = Object.values(allStops)
                .filter((stop) => stop.duplicate === -1 && stop?.stopName?.toLowerCase().includes(searchLower))
                .slice(0, limit)
                .map((stop) => ({
                    value: `${stop.stopName}|${stop.lat}|${stop.lon}`,
                    label: <DisplayStop stop={stop} noWarning={true} noCode={true} noRoutes={true} noInterchange={true} showOpenInNewTab={true} />,
                    coordinates: [stop.lon, stop.lat],
                    address: stop.stopName,
                    type: 'stop',
                    ...stop
                }));
            
            localOptions = matchingStops;
            // Set options immediately with local results
            setOptions(localOptions);
        }

        try {
            // Then fetch and append Geoapify results
            const response = await fetch(`https://api.geoapify.com/v1/geocode/search?text=${encodeURIComponent(value)}&apiKey=${config.maps.geoapify.ctrxs}&filter=countrycode:au&limit=${limit}`);
            const geoapifyData = await response.json();

            if (geoapifyData.features) {
                const geoapifyOptions = geoapifyData.features.map((feature) => {
                    const address = feature.properties.formatted;
                    const coordinates = `${feature.properties.lat}|${feature.properties.lon}`;
                    const value = `${address}|${coordinates}`;
                    const stopName = feature.properties.address_line1 || feature.properties.name;
                    
                    const label = address;
                    
                    return {
                        value,
                        label,
                        coordinates: [feature.properties.lon, feature.properties.lat],
                        name: feature.properties.name || address,
                        stopName,
                        address: address,
                        type: 'address',
                        ...feature.properties
                    };
                });
                
                // Combine local and Geoapify results
                setOptions([...localOptions, ...geoapifyOptions]);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, 500);

    // Update search value when user types
    const handleSearch = useCallback(value => {
        setSearchValue(value);
        debouncedSearch(value);
    }, [debouncedSearch]);

    return (
        <Select
            showSearch
            loading={loading}
            placeholder="Search for a location, point of interest, or bus stop"
            defaultActiveFirstOption={false}
            filterOption={false}
            value={searchValue?.length ? searchValue : location?.value}
            onSearch={handleSearch}
            notFoundContent={null}
            options={options}
            onSelect={async (value, option) => {
                if (option.type === 'address') {
                    setSearchValue(null)
                    setLoading(true);
                    const {lat, lon} = await snapToRoadOsrm(option);
                    // Check lat/lon are valid numbers and within range
                    if (isNaN(lat) || isNaN(lon) || 
                        lat < -90 || lat > 90 || 
                        lon < -180 || lon > 180) {
                        console.error('Invalid snapped coordinates:', {lat, lon});
                        setLoading(false);
                        return;
                    }
                    option.coordinates = [lon, lat];
                    option.lat = lat;
                    option.lon = lon;
                    setLoading(false);
                }
                setLocation(value, option);
            }}
            {...props}
        />
    );
};

export default LocationLookup; 